import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export interface TableColumn<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  on_row_click?: (row: T) => void;
  is_loading?: boolean;
  empty_message?: string;
  sort_config?: {
    key: string;
    direction: 'asc' | 'desc';
  };
  on_sort?: (key: string) => void;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  className?: string;
}

export function Table<T extends Record<string, any>>({
  data,
  columns,
  on_row_click,
  is_loading = false,
  empty_message = 'No data available',
  sort_config,
  on_sort,
  striped = true,
  hoverable = true,
  compact = false,
  className = ''
}: TableProps<T>) {
  const handle_sort = (column: TableColumn<T>) => {
    if (column.sortable && on_sort) {
      on_sort(column.key);
    }
  };
  
  const get_sort_icon = (column: TableColumn<T>) => {
    if (!column.sortable) return null;
    
    if (sort_config?.key === column.key) {
      return sort_config.direction === 'asc' 
        ? <ChevronUp size={16} />
        : <ChevronDown size={16} />;
    }
    
    return <ChevronsUpDown size={16} className="text-gray-400" />;
  };
  
  const get_alignment_class = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  };
  
  const cell_padding = compact ? 'px-4 py-2' : 'px-6 py-4';
  
  return (
    <div className={`overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`
                  ${cell_padding} text-xs font-semibold text-gray-700 uppercase tracking-wider
                  ${get_alignment_class(column.align)}
                  ${column.sortable ? 'cursor-pointer select-none hover:bg-gray-100 transition-smooth' : ''}
                `}
                style={{ width: column.width }}
                onClick={() => handle_sort(column)}
              >
                <div className="flex items-center gap-2 justify-start">
                  <span>{column.header}</span>
                  {get_sort_icon(column)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        
        <tbody className="bg-white divide-y divide-gray-200">
          {is_loading ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-12 text-center">
                <div className="flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest-green" />
                  <span className="text-gray-500">Loading data...</span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                {empty_message}
              </td>
            </tr>
          ) : (
            data.map((row, row_index) => (
              <tr
                key={row.id || row_index}
                className={`
                  ${striped && row_index % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
                  ${hoverable ? 'hover:bg-forest-green hover:bg-opacity-5 transition-smooth' : ''}
                  ${on_row_click ? 'cursor-pointer' : ''}
                `}
                onClick={() => on_row_click?.(row)}
              >
                {columns.map((column) => (
                  <td
                    key={`${row.id || row_index}-${column.key}`}
                    className={`${cell_padding} text-sm text-gray-900 ${get_alignment_class(column.align)}`}
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Pagination Component
interface PaginationProps {
  current_page: number;
  total_pages: number;
  on_page_change: (page: number) => void;
  items_per_page: number;
  total_items: number;
}

export const Pagination: React.FC<PaginationProps> = ({
  current_page,
  total_pages,
  on_page_change,
  items_per_page,
  total_items
}) => {
  const start_item = (current_page - 1) * items_per_page + 1;
  const end_item = Math.min(current_page * items_per_page, total_items);
  
  const get_page_numbers = () => {
    const pages: (number | string)[] = [];
    const max_visible = 5;
    
    if (total_pages <= max_visible) {
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (current_page > 3) {
        pages.push('...');
      }
      
      for (let i = Math.max(2, current_page - 1); i <= Math.min(total_pages - 1, current_page + 1); i++) {
        pages.push(i);
      }
      
      if (current_page < total_pages - 2) {
        pages.push('...');
      }
      
      pages.push(total_pages);
    }
    
    return pages;
  };
  
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => on_page_change(current_page - 1)}
          disabled={current_page === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => on_page_change(current_page + 1)}
          disabled={current_page === total_pages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{start_item}</span> to{' '}
            <span className="font-medium">{end_item}</span> of{' '}
            <span className="font-medium">{total_items}</span> results
          </p>
        </div>
        
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            {get_page_numbers().map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    ...
                  </span>
                );
              }
              
              return (
                <button
                  key={page}
                  onClick={() => on_page_change(page as number)}
                  className={`
                    relative inline-flex items-center px-4 py-2 border text-sm font-medium
                    ${
                      current_page === page
                        ? 'z-10 bg-forest-green border-forest-green text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  {page}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
};